import { Component, ElementRef, OnInit, ViewChild, Renderer2 } from '@angular/core';
import { getDocument } from 'pdfjs-dist/build/pdf';
import { ModalService } from '../../../services/modal/modal.service';
import { BaseModalComponent } from '../../modals/base-modal.component';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { Controller as CoverpathClientController } from '../../../../../coverpath-client/public/js/Controller';
import { Utils } from '../../../../../common/js/Utils';
import { Product } from '../../../../../common/js/static/Product';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import type { CoverpathBasePolicy } from '../../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { IsLoadingType } from '../../../interfaces/account-center/IsLoadingDataInterface';
import { PolicyData } from '../../../interfaces/PolicyData';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';

interface FormToSign {
  formType: string;
  pdfUrl: string;
  textPrompt: string;

  signFn(): void;
}

@Component({
  selector: 'client-esign',
  moduleId: module.id,
  templateUrl: './client-esign-modal.component.html',
})
export class ClientEsignModalComponent extends BaseModalComponent implements OnInit {
  cancel() {}
  confirm() {}

  controller: CoverpathClientController;

  @ViewChild('pdfCanvasContainer') pdfCanvasContainer: ElementRef;

  formsToSign: Array<FormToSign> = [];
  loadingScreen: boolean = false;
  esignDisabled = true;
  pdfRendered = false;
  errorRenderingPdf = false;
  errorSigningPdf = false;
  docIndex: number = 0;
  primaryPolicyNumber: string;

  constructor(
    private amorphicService: AmorphicService,
    protected modalService: ModalService,
    private renderer: Renderer2,
    protected accountCenterService: AccountCenterService
  ) {
    super(modalService);
    this.controller = amorphicService.controller as CoverpathClientController;
  }

  async ngOnInit() {
    this.primaryPolicyNumber = this.accountCenterService.primaryPolicy.policyNumber;
    const primaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(
      this.accountCenterService.primaryPolicy.__id__ as string
    );

    if (!primaryPolicyData.getCACIsWholeLife()) {
      await this.controller.accountCenterController.deleteExistingRBIIfExists();
    }

    this.formsToSign = await this.determineFormsToSign(this.primaryPolicyNumber, primaryPolicyData);

    if (this.formsToSign.length === 0) {
      const error = {
        isHumanRelated: false,
        data: Utils.extractIds(this.accountCenterService.primaryPolicy, this.controller.customer),
      };
      UILoggerSingleton.getInstance().error({
        module: 'ClientEsignModalComponent',
        function: 'ngOnInit',
        category: UICategory.request,
        error: error,
      });

      this.closeModal();
    } else {
      this.docIndex = 0;
      await this.showPdf();
    }
  }

  async determineFormsToSign(primaryPolicyNumber: string, primaryPolicyData: PolicyData): Promise<Array<FormToSign>> {
    const formsToSign: Array<FormToSign> = [];
    const needsToSignInsurabilityForm =
      await this.controller.accountCenterController.customerNeedsToSignInsurabilityForm(primaryPolicyNumber);

    const needsToSignMecForm = await this.controller.accountCenterController.customerNeedsToSignMecForm(
      primaryPolicyNumber
    );

    if (needsToSignMecForm) {
      formsToSign.push({
        formType: 'Mec',
        pdfUrl: this.controller.accountCenterController.getViewMecFormURL(),
        textPrompt: `The applied for whole life policy has been determined to be a Modified Endowment Contract. Please read and sign the following notice and acknowledgement.        `,
        signFn: async () => {
          await this.controller.accountCenterController.getAndSignForm(primaryPolicyNumber, 'Mec');
        },
      });
    }

    if (needsToSignInsurabilityForm) {
      const isCASPolicy = primaryPolicyData.getCACIsCoverpathPolicy();
      const phoneNumber = isCASPolicy ? Product.channels.CAS.phone : Product.channels.MMDirect.phone;
      const helpEmail = isCASPolicy ? Product.channels.CAS.helpEmail : Product.channels.MMDirect.helpEmail;

      formsToSign.push({
        formType: 'Insurability',
        pdfUrl: this.controller.accountCenterController.getViewInsurabilityFormURL(),
        textPrompt: `
        <p>As you respond to the insurability statement provided, please consider the following:  If you have been tested for COVID-19 or sought medical advice for COVID-19 symptoms (fever, cough, cold, flu–like, or shortness of breath), it qualifies as having sought or received treatment. If you have had these symptoms and called a health professional, then you have sought advice from a medical professional.  Please include this information in the disclosure of exceptions.</p>
        <br>
        <p>Finally, if you need to provide details about a change in health, including if you have been exposed to someone who tested positive for COVID-19 or if you have sought testing or medical advice, please reach out to our support team at <a href="mailto:${helpEmail}">${helpEmail}</a>
        or call us at <a href="tel:${phoneNumber}">${phoneNumber}</a> (9am-6pm Eastern Time, Monday-Friday).</p>
        `,
        signFn: async () => {
          await this.controller.accountCenterController.getAndSignForm(primaryPolicyNumber, 'Insurability');
        },
      });
    }

    const needsToSignRevisedBasicIllustration =
      await this.controller.accountCenterController.customerNeedsToSignRevisedBasicIllustration(primaryPolicyNumber);

    if (needsToSignRevisedBasicIllustration) {
      formsToSign.push({
        formType: 'RevisedBasicIllustration',
        pdfUrl: this.controller.accountCenterController.getViewRevisedBasicIllustrationURL(),
        textPrompt:
          'Your policy details have changed compared to your original application. Please sign your updated Whole Life illustration.',
        signFn: async () => {
          await this.controller.accountCenterController.getAndSignForm(primaryPolicyNumber, 'RevisedBasicIllustration');
        },
      });
    }

    // Always keep the amendments form last since amended values are copied to policy here
    let secondaryPolicyData;
    if (primaryPolicyData && primaryPolicyData.getCACIsJointApp()) {
      const secondaryPolicyId = this.accountCenterService.application.secondaryPolicyWithPendingAmendment.__id__;
      secondaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(secondaryPolicyId);
    }

    const secondaryPolicyNeedsAmendmentForm = secondaryPolicyData && secondaryPolicyData.getCACHasPendingAmendment();
    const primaryPolicyNeedsAmendmentForm = primaryPolicyData.getCACHasPendingAmendment();
    const needsToSignAmendmentForm =
      (primaryPolicyNeedsAmendmentForm || secondaryPolicyNeedsAmendmentForm) &&
      !primaryPolicyData.getCACIsAccelerated();

    if (needsToSignAmendmentForm) {
      formsToSign.push({
        formType: 'Amendments',
        pdfUrl: this.controller.accountCenterController.getViewAmendmentsFormURL(),
        textPrompt:
          'Your policy details have changed compared to your original application. Please sign to acknowledge these changes.',
        signFn: async () => {
          await this.controller.accountCenterController.acceptAmendedPolicy(primaryPolicyNumber);
        },
      });
    }

    return formsToSign;
  }

  closeModal(): void {
    this.modalService.deactivate();
  }

  async showPdf() {
    const formToSign = this.formsToSign[this.docIndex];

    try {
      this.loadingScreen = true;

      const clientPdf = await getDocument(formToSign.pdfUrl);
      await this.renderPages(clientPdf);

      this.pdfRendered = true;
      this.esignDisabled = false;
    } catch (e) {
      const error = {
        isHumanRelated: false,
        name: e.name,
        message: e.message,
        stack: e.stack,
        data: {
          formType: formToSign.formType,
          policyNumber: this.primaryPolicyNumber,
          ...Utils.extractIds(this.accountCenterService.primaryPolicy, this.controller.customer),
        },
      };
      UILoggerSingleton.getInstance().error({
        module: 'ClientEsignModalComponent',
        function: 'showPdf',
        category: UICategory.request,
        error: error,
      });

      this.errorRenderingPdf = true;
    } finally {
      this.loadingScreen = false;
    }
  }

  async renderPages(pdfDoc) {
    //first remove any existing children elements to the container (which would be present from a previous pdf)
    this.pdfCanvasContainer.nativeElement.innerHTML = '';

    for (let num = 1; num <= pdfDoc.numPages; num++) {
      const currentPage = await pdfDoc.getPage(num);

      const scale = 1.25;
      const viewport = currentPage.getViewport(scale);

      const child = this.renderer.createElement('canvas');
      this.renderer.addClass(child, 'client-esign__pdf-canvas');
      this.renderer.setProperty(child, 'height', viewport.height);
      this.renderer.setProperty(child, 'width', viewport.width);

      this.pdfCanvasContainer.nativeElement.appendChild(child);

      const context = (child as HTMLCanvasElement).getContext('2d');
      const renderContext = {
        canvasContext: context,
        viewport,
      };

      await currentPage.render(renderContext);
    }
  }

  async signForm() {
    try {
      this.loadingScreen = true;
      this.esignDisabled = true;
      UILoggerSingleton.getInstance().info(
        {
          component: 'browser',
          module: 'ClientEsignModalComponent',
          function: 'signForm',
          category: UICategory.request,
          data: {
            formType: this.formsToSign[this.docIndex].formType,
            policyNumber: this.primaryPolicyNumber,
            ...Utils.extractIds(this.accountCenterService.primaryPolicy, this.controller.customer),
          },
        },
        'singing forms'
      );
      await this.formsToSign[this.docIndex].signFn();
      this.docIndex++;

      //show the next form if there is one, or accept the policy if thr last form was just signed
      if (this.docIndex < this.formsToSign.length) {
        await this.showPdf();
      } else {
        this.docIndex--;
        const primaryPolicy = this.accountCenterService.primaryPolicy as CoverpathBasePolicy;
        UILoggerSingleton.getInstance().info(
          {
            component: 'browser',
            module: 'ClientEsignModalComponent',
            function: 'signForm',
            category: UICategory.request,
            data: {
              policyNumber: this.primaryPolicyNumber,
              ...Utils.extractIds(primaryPolicy, this.controller.customer),
            },
          },
          'accepting Offer'
        );

        this.accountCenterService.isLoadingSubject.next({
          isLoading: true,
          type: IsLoadingType.DURING,
          action: this.accountCenterService.latestActionString,
        });
        await this.accountCenterService.acceptFromService();

        UILoggerSingleton.getInstance().info({
          component: 'browser',
          module: 'ClientEsignModalComponent',
          function: 'publicSave()',
          category: UICategory.request,
          data: {
            policyNumber: this.primaryPolicyNumber,
            ...Utils.extractIds(primaryPolicy, this.controller.customer),
          },
        });
        await this.controller.publicSave();

        this.closeModal();
      }
    } catch (e) {
      const error = {
        isHumanRelated: false,
        name: e.name,
        message: e.message,
        stack: e.stack,
        data: {
          formType: this.formsToSign[this.docIndex].formType,
          policyNumber: this.primaryPolicyNumber,
          ...Utils.extractIds(this.accountCenterService.primaryPolicy, this.controller.customer),
        },
      };
      UILoggerSingleton.getInstance().error({
        module: 'ClientEsignModalComponent',
        function: 'signForm',
        category: UICategory.request,
        error: error,
      });

      this.errorSigningPdf = true;
    } finally {
      this.loadingScreen = false;
    }
  }
}
